import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["options"];
import { useMemo, useEffect } from 'react';
import fuzzy from 'fuzzy';
import { useQuery } from 'data-fetching-client';
import I18n from 'I18n';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { View } from 'foundations-components/components/View';
import Trackables from 'automation-ui-usage-tracking/Trackables';
import { trackEvent } from 'automation-ui-canvas/utils/trackingUtils';
import { UNKNOWN_ID } from 'automation-ui-references/object-types/CRMObjectTypes';
import SelectionPanel from 'automation-ui-canvas/create/SelectionPanel';
import WorkflowTypeSelect from 'automation-ui-enrollment/components/workflowType/WorkflowTypeSelect';
import { GET_OBJECT_PROPERTIES_BY_GROUP } from 'automation-ui-references/object-type-properties/ObjectTypePropertyQueries';
import { convertToAddonPropertyOption } from 'automation-ui-action-features/object-type-properties/propertyUtils';
import { dataTypeToIconName } from 'automation-ui-formula-editor/data-sources/utils/getPropertyIcon';
import sortByText from 'automation-ui-enrollment/utils/sortByText';
import { useIsUngated } from 'automation-ui-core/hooks/authHooks';
import { SUPPORT_SELECTING_SENSITIVE_PROPERTIES } from 'automation-ui-canvas/constants/Gates';
import { jsx as _jsx } from "react/jsx-runtime";
export default function PropertySelectionObjectSpecificPanel({
  objectTypeId,
  setObjectTypeId,
  onClose,
  stagedTriggerOption,
  onSelectTriggerOption,
  searchTerm,
  setSearchTerm
}) {
  useEffect(() => {
    trackEvent(Trackables.VIEWED_PROPERTY_TRIGGER_SELECTION_PANEL, {
      type: 'object-specific',
      objectTypeId
    });
  }, [objectTypeId]);
  const isGatedToSupportSelectingSensitiveProperties = !useIsUngated(SUPPORT_SELECTING_SENSITIVE_PROPERTIES);
  const {
    data: propertyData,
    loading
  } = useQuery(GET_OBJECT_PROPERTIES_BY_GROUP, {
    variables: {
      objectTypeId
    },
    skip: objectTypeId === UNKNOWN_ID
  });
  const allPropertyOptions = useMemo(() => {
    var _propertyData$automat;
    return sortByText(((_propertyData$automat = propertyData === null || propertyData === void 0 ? void 0 : propertyData.automation_propertiesByGroup) !== null && _propertyData$automat !== void 0 ? _propertyData$automat : []).map(({
      name,
      displayName,
      properties,
      displayOrder
    }) => ({
      name,
      text: displayName,
      sortOrder: displayOrder,
      options: sortByText(properties.filter(property => !property.hidden).map(property => {
        var _option$icon;
        const option = convertToAddonPropertyOption({
          property,
          objectTypeId,
          enforceFlp: true,
          dataSensitivityAllowlist: isGatedToSupportSelectingSensitiveProperties ? ['none'] : undefined
        });
        option.icon = (_option$icon = option.icon) !== null && _option$icon !== void 0 ? _option$icon : dataTypeToIconName(property.type);
        return option;
      }))
    })));
  }, [propertyData === null || propertyData === void 0 ? void 0 : propertyData.automation_propertiesByGroup, objectTypeId, isGatedToSupportSelectingSensitiveProperties]);
  const objectPropertySearchResults = allPropertyOptions.map(_ref => {
    let {
        options
      } = _ref,
      rest = _objectWithoutPropertiesLoose(_ref, _excluded);
    return Object.assign({
      options: fuzzy.filter(searchTerm, options, {
        extract: ({
          text
        }) => text
      }).sort(({
        score: scoreA
      }, {
        score: scoreB
      }) => scoreB - scoreA).map(({
        original
      }) => original)
    }, rest);
  });
  return /*#__PURE__*/_jsx(SelectionPanel, {
    heading: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "createWorkflow.propertySelectionPanel.title"
    }),
    additionalFilters: /*#__PURE__*/_jsx(View, {
      marginBlockStart: "200",
      children: /*#__PURE__*/_jsx(WorkflowTypeSelect, {
        objectTypeId: objectTypeId,
        onChange: setObjectTypeId,
        placeholder: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "createWorkflow.objectSelectPlaceholder"
        }),
        noneOptionLabel: I18n.text('createWorkflow.objectTypeSelectionPanel.default.property'),
        objectTypeFilter: () => true
      })
    }),
    options: objectTypeId === UNKNOWN_ID ? [] : objectPropertySearchResults,
    loading: loading,
    searchTerm: searchTerm,
    onSearchTermChange: setSearchTerm,
    onClose: onClose,
    onSelectOption: ({
      value
    }) => {
      trackEvent(Trackables.SELECTED_PROPERTY_TRIGGER, {
        type: 'object-specific',
        objectTypeId
      });
      onSelectTriggerOption(Object.assign({}, stagedTriggerOption, {
        filters: [{
          filterType: 'PROPERTY',
          property: 'hs_name',
          operation: {
            propertyType: 'string',
            operator: 'IS_EQUAL_TO',
            value,
            defaultValue: null,
            includeObjectsWithNoValueSet: false,
            operationType: 'string',
            operatorName: 'IS_EQUAL_TO'
          },
          frameworkFilterId: null
        }]
      }));
    }
  });
}